/* eslint-disable react-hooks/exhaustive-deps */
import { useTooltip } from "context/TooltipProvider";
import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { editAppealRequest2 } from "services/appealService";
import { AppealRequest } from "types";
import { LoadingIcon } from "utils/constant";
import { errorHandlerOnForm } from "utils/helper";

export type CourtField = {
  name: string;
  label: string;
  type?: "text" | "date";
  required?: boolean;
};

type Props = {
  appeal?: AppealRequest;
  icon?: string;
  title: string;
  fields: CourtField[];
  onSaved: () => void;
};

const toDateInput = (v: any) => {
  if (!v) return "";
  try {
    return new Date(v).toISOString().split("T")[0];
  } catch {
    return "";
  }
};

// Inline panel (replaces the registrar lower/higher court modal): renders the
// court fields directly in the Case Actions section.
const RegistrarCourtPanel: FC<Props> = ({
  appeal,
  icon = "🏛️",
  title,
  fields,
  onSaved,
}) => {
  const { t } = useTranslation();
  const { showTooltip } = useTooltip();
  const [loading, setLoading] = useState(false);
  const [values, setValues] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!appeal) return;
    const init: Record<string, string> = {};
    fields.forEach((f) => {
      const v = (appeal as any)?.[f.name];
      init[f.name] = f.type === "date" ? toDateInput(v) : (v ?? "");
    });
    setValues(init);
  }, [appeal]);

  const handleSubmit = () => {
    for (const f of fields) {
      if (f.required && !values[f.name]) {
        showTooltip(`${f.label} ${t("is_required", { defaultValue: "is required" })}`, "error");
        return;
      }
    }
    setLoading(true);
    const payload: Record<string, any> = { id: appeal?.id };
    fields.forEach((f) => {
      const v = values[f.name];
      if (v === "" || v == null) return;
      // Send ISO datetime for date fields (backend expects datetime format).
      payload[f.name] = f.type === "date" ? new Date(v).toISOString() : v;
    });
    editAppealRequest2(payload)
      .then(() => {
        showTooltip(t("successfully_sent"), "success");
        onSaved();
      })
      .catch((err) =>
        showTooltip(
          err?.response?.data?.detail || errorHandlerOnForm(err),
          "error",
        ),
      )
      .finally(() => setLoading(false));
  };

  return (
    <div className="rounded-xl border border-gray-200 bg-gray-50/60 p-4 space-y-3">
      <div className="flex items-center gap-2">
        <span className="text-base">{icon}</span>
        <h4 className="text-sm font-semibold text-gray-900">{title}</h4>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {fields.map((f) => (
          <div key={f.name} className="flex flex-col">
            <label className="text-xs font-medium text-gray-600 mb-1">
              {f.label}
              {f.required && <span className="text-red-500"> *</span>}
            </label>
            <input
              type={f.type === "date" ? "date" : "text"}
              value={values[f.name] ?? ""}
              onChange={(e) =>
                setValues((p) => ({ ...p, [f.name]: e.target.value }))
              }
              className="border border-gray-300 rounded-lg bg-white p-2 text-sm focus:outline-none focus:ring focus:ring-red-200"
            />
          </div>
        ))}
      </div>

      <div className="flex justify-end">
        <button
          type="button"
          onClick={handleSubmit}
          disabled={loading}
          className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-red-600 text-white text-sm font-semibold rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? (
            <>
              {LoadingIcon}
              {t("loading")}
            </>
          ) : (
            t("save", { defaultValue: "Save" })
          )}
        </button>
      </div>
    </div>
  );
};

export default RegistrarCourtPanel;
